import React, { ReactElement, useContext } from "react";
import { DEFAULT_VALUE, jsonKeyMapping } from "../../../Config/Constants";

import InputContext from "../../../Contexts/Input.Context";
import useLoadMoreFields from "../../../Hooks/LoadMoreFields";
import { TemplateObject } from "../../../Models/TemplateObject";
import FormBuilder from "../../FormBuilder/FormBuilder";
import ToolTipWithLabelComponent from "../ToolTipWithLabelComponent";

type Props = {
  children: React.ReactNode;
  formElement: TemplateObject;
  label: string;
  labelsub: string;
  subtext: string;
};

const InputWrapper = ({
  children,
  formElement,
  label,
  labelsub,
  subtext,
}: Props): ReactElement => {
  const { conditionalInput, disableAutoAdvance } = useContext(InputContext);

  // Helper function to determine if a label should be hidden
  const shouldHideLabel = (
    labelText: string,
    formElement?: TemplateObject
  ): boolean => {
    if (!labelText) return false;

    // Check if this is a radio or checkbox field - these should NEVER be hidden
    const fieldType = formElement?.[jsonKeyMapping.FORM_TYPE] || "";
    if (fieldType === "input-radio" || fieldType === "input-checkbox") {
      return false;
    }

    // If the field has a placeholder, the label can be hidden (placeholder serves as the guide)
    const hasPlaceholder =
      formElement?.placeholder && formElement.placeholder.trim() !== "";

    // If NO placeholder exists, ALWAYS show the label (it serves as the question/instruction)
    if (!hasPlaceholder) {
      return false;
    }

    // If there IS a placeholder, check if this looks like a simple field label that can be hidden
    const cleanLabel = labelText
      .trim()
      .replace(/[:\s]+$/, "")
      .toLowerCase();

    // Simple field patterns (these can be hidden when placeholder exists)
    const simpleFieldPatterns = [
      /^(first\s*name|last\s*name|full\s*name|name)[\s\:]*$/i,
      /^(email|email\s*address)[\s\:]*$/i,
      /^(phone|phone\s*number|telephone)[\s\:]*$/i,
      /^(address|street\s*address)[\s\:]*$/i,
      /^(city|town)[\s\:]*$/i,
      /^(state|province)[\s\:]*$/i,
      /^(zip|zip\s*code|zipcode|postal\s*code)[\s\:]*$/i,
      /^(date|birth\s*date|dob)[\s\:]*$/i,
      /^(company|organization)[\s\:]*$/i,
      /^(title|job\s*title)[\s\:]*$/i,
      /^(age|gender)[\s\:]*$/i,
      /^(fax|fax\s*number)[\s\:]*$/i,
      /^(name\s*of\s*the\s*vendor)[\s\:]*$/i,
    ];

    // Question patterns (keep these visible even with placeholder)
    const questionPatterns = [
      /what\s+is\s+/i,
      /what\s+are\s+/i,
      /how\s+/i,
      /when\s+/i,
      /where\s+/i,
      /why\s+/i,
      /which\s+/i,
      /\?/,
      /please\s+(enter|provide|specify)/i,
      /do\s+you\s+/i,
      /are\s+you\s+/i,
      /will\s+you\s+/i,
      /should\s+/i,
      /would\s+you\s+/i,
    ];

    // If it's a question, keep it visible even if there's a placeholder
    if (questionPatterns.some((pattern) => pattern.test(labelText))) {
      return false;
    }

    // If it matches simple field patterns AND has a placeholder, hide it
    if (simpleFieldPatterns.some((pattern) => pattern.test(cleanLabel))) {
      return true;
    }

    // If it's very short (likely a simple field name) AND has placeholder, hide it
    if (hasPlaceholder && cleanLabel.length <= 20 && !labelText.includes("?")) {
      return true;
    }

    return false;
  };

  // Determine label class and inline styles for immediate hiding
  const getLabelClassAndStyle = (
    labelText: string
  ): { className: string; style?: React.CSSProperties } => {
    const baseClass = "form-label text-right d-inline";

    // Always preserve question labels
    if (labelText.includes("?") || labelText.toLowerCase().includes("what")) {
      return { className: `${baseClass} question-label` };
    }

    // Check for radio/checkbox context (these should never be hidden)
    if (
      formElement[jsonKeyMapping.FORM_TYPE] === "radio" ||
      formElement[jsonKeyMapping.FORM_TYPE] === "checkbox"
    ) {
      return {
        className: `${baseClass} input-${
          formElement[jsonKeyMapping.FORM_TYPE]
        }-label`,
      };
    }

    // Apply label hiding logic with immediate inline styles
    if (shouldHideLabel(labelText, formElement)) {
      return {
        className: `${baseClass} simple-field-label`,
        style: {
          opacity: 0,
          height: 0,
          margin: 0,
          padding: 0,
          overflow: "hidden",
          visibility: "hidden" as const,
          display: "none",
          transition: "none",
        },
      };
    }

    return { className: baseClass };
  };

  /* Load More Fields Based Upon Previous Selected Values */
  useLoadMoreFields({ formElement });
  return (
    <div
      className={`${
        formElement[jsonKeyMapping.FORM_ADD_MORE] ? "" : "form-group"
      } ${formElement.class ? formElement.class : ""}`}
      style={
        formElement?.id === jsonKeyMapping.STATE_SPECIFIC_STATE
          ? { marginTop: 8 }
          : { marginTop: 0, float: "left", paddingRight: "8px" }
      }
    >
      {formElement[jsonKeyMapping.FORM_SUB_TOOLTIP] ? (
        <ToolTipWithLabelComponent
          tooltipClass="tooltip-bulb"
          labelClass={"form-label text-right"}
          formElement={formElement}
        />
      ) : (
        <>
          {labelsub !== "" && subtext !== "" ? (
            <>
              <div>
                <label
                  className={getLabelClassAndStyle(labelsub).className}
                  style={getLabelClassAndStyle(labelsub).style}
                  data-field-type={
                    shouldHideLabel(labelsub || "", formElement)
                      ? "simple"
                      : "complex"
                  }
                  title={labelsub}
                >
                  {labelsub}
                </label>
                <small className="form-text text-muted subtext  d-md-block">
                  {subtext}
                </small>
              </div>{" "}
            </>
          ) : (
            ""
          )}
          <label
            className={getLabelClassAndStyle(label).className}
            style={getLabelClassAndStyle(label).style}
            data-field-type={
              shouldHideLabel(label || "", formElement) ? "simple" : "complex"
            }
            title={label}
          >
            {label}
          </label>
          {labelsub === "" && subtext !== "" ? (
            <>
              <div>
                <small className="form-text text-muted subtext  d-md-block">
                  {subtext}
                </small>
              </div>
            </>
          ) : (
            ""
          )}
        </>
      )}
      {formElement[jsonKeyMapping.FORM_SUB_LABEL] && (
        <div>{formElement[jsonKeyMapping.FORM_SUB_LABEL]}</div>
      )}
      <div className="mt-2"></div>
      <div>{children}</div>
      {conditionalInput && (
        <FormBuilder
          formGroup={conditionalInput.formElement}
          hasParent={{
            [jsonKeyMapping.FORM_ID]: formElement[jsonKeyMapping.FORM_ID],
            [jsonKeyMapping.FORM_LABEL]: conditionalInput.parentLabel,
            showElement: conditionalInput.value || DEFAULT_VALUE,
          }}
          disableAutoAdvance={disableAutoAdvance}
        />
      )}
    </div>
  );
};

export default InputWrapper;
